import { useEffect, useState, useMemo, useRef } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STATUSES, StatusBadge, formatDate } from "@/lib/status";
import { Search, Filter, X, RotateCcw, Download, ChevronRight, ChevronLeft, ArrowUpDown, ArrowUp, ArrowDown, ChevronsLeft, ChevronsRight, Trash2, Loader2, Settings2, Check, RefreshCw } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import PullToRefresh from "@/components/PullToRefresh";

const ALL_COLUMNS = [
  { key: "request_no", label: "Talep No", required: true },
  { key: "created_at", label: "Tarih" },
  { key: "user_name", label: "Kullanıcı" },
  { key: "assigned_to_name", label: "Hazırlayan" },
  { key: "supplier_name", label: "Tedarikçi" },
  { key: "product_code", label: "Ürün Kodu", required: true },
  { key: "product_name", label: "Açıklama" },
  { key: "quantity", label: "Miktar", required: true },
  { key: "unit", label: "Birim" },
  { key: "reason", label: "Sebep", adminOnly: true },
  { key: "internal_note", label: "İç Not" },
  { key: "status", label: "Durum", required: true },
];

const DEFAULT_VISIBLE = ALL_COLUMNS.map((c) => c.key);
const STORAGE_KEY_COLS = "talepler.visibleCols.v3";
// Persist pagination/sort per tab session so returning from a detail page
// restores the page the user was on (fixes "back always jumps to page 1").
const SS_PAGE = "talepler.page";
const SS_PAGE_SIZE = "talepler.pageSize";
const SS_SORT = "talepler.sortBy";

const emptyFilters = {
  status: "",
  supplier_id: "",
  user_id: "",
  product_code: "",
  date_from: "",
  date_to: "",
  only_open: false,
  q: "",
};

const DATE_PRESETS = [
  { key: "today", label: "Bugün" },
  { key: "7", label: "Son 7 gün" },
  { key: "30", label: "Son 30 gün" },
  { key: "month", label: "Bu ay" },
];

export default function RequestsListPage() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Gate the first data fetch until the saved scope ("Benim Taleplerim / Tümü")
  // has been resolved, so we never fire a spurious "all" request that could win
  // an async race against the correct "mine" request on refresh / fresh login.
  const [scopeReady, setScopeReady] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState(() => {
    try { const s = sessionStorage.getItem(SS_SORT); if (s) return JSON.parse(s); } catch (_) {}
    return { field: "created_at", dir: "desc" };
  });
  const [page, setPage] = useState(() => {
    const s = Number(sessionStorage.getItem(SS_PAGE));
    return s && s > 0 ? s : 1;
  });
  const [pageSize, setPageSize] = useState(() => {
    const s = Number(sessionStorage.getItem(SS_PAGE_SIZE));
    return s && s > 0 ? s : 25;
  });
  const [selected, setSelected] = useState(new Set()); // request IDs (not line IDs)
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  // Visible columns (persisted in localStorage)
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_COLS);
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return DEFAULT_VISIBLE;
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY_COLS, JSON.stringify(visibleCols)); } catch (_) {}
  }, [visibleCols]);
  const isColVisible = (key) => visibleCols.includes(key);
  const toggleCol = (key) => {
    setVisibleCols((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const isAdmin = user?.role === "admin";
  // Staff (admin/purchasing/warehouse) can act on all; a regular "user" only on their own.
  const canSelectRow = (row) => user?.role !== "user" || row.user_id === user?.id;

  // "Benim Taleplerim / Tümü" hızlı görünümü. Hazırlayıcılar kalabalık listede
  // kendi taleplerini kolayca bulsun diye tercih localStorage'da hatırlanır ve
  // varsayılan "benim taleplerim"dir.
  const applyScope = (s) => {
    try { localStorage.setItem("requests_scope", s); } catch (_) { /* noop */ }
    setFilters((f) => ({ ...f, user_id: s === "mine" ? (user?.id || "") : "" }));
  };
  const scopeApplied = useRef(false);
  useEffect(() => {
    if (!user?.id || scopeApplied.current) return;
    scopeApplied.current = true;
    let saved = "mine";
    try { saved = localStorage.getItem("requests_scope") || "mine"; } catch (_) { /* noop */ }
    if (saved === "mine") setFilters((f) => ({ ...f, user_id: user.id }));
    // Scope resolved — release the data-fetch gate (set in the SAME batch as the
    // filter above, so the first load() runs once with the correct user_id).
    setScopeReady(true);
  }, [user?.id]);

  // Monotonic sequence guard: only the most recent load() may write state, so a
  // slow earlier response can never clobber a newer one (the root cause of the
  // "Benim Taleplerim selected but all shown" bug).
  const loadSeq = useRef(0);
  const load = async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.supplier_id) params.supplier_id = filters.supplier_id;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.product_code) params.product_code = filters.product_code;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.only_open) params.only_open = true;
      const { data } = await api.get("/requests", { params });
      if (seq === loadSeq.current) setRequests(data);
    } catch (e) {
      if (seq === loadSeq.current) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, u] = await Promise.all([
          api.get("/suppliers"),
          isAdmin ? api.get("/users") : Promise.resolve({ data: [] }),
        ]);
        setSuppliers(s.data);
        setUsers(u.data);
      } catch (_) {}
    })();
  }, [isAdmin]);

  useEffect(() => { if (scopeReady) load(); /* eslint-disable-next-line */ },
    [scopeReady, filters.status, filters.supplier_id, filters.user_id, filters.product_code,
     filters.date_from, filters.date_to, filters.only_open]);

  const applyDatePreset = (key) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    let from = "", to = fmt(today);
    if (key === "today") from = fmt(today);
    else if (key === "7") { const d = new Date(); d.setDate(d.getDate() - 6); from = fmt(d); }
    else if (key === "30") { const d = new Date(); d.setDate(d.getDate() - 29); from = fmt(d); }
    else if (key === "month") from = fmt(new Date(today.getFullYear(), today.getMonth(), 1));
    setFilters((f) => ({ ...f, date_from: from, date_to: to }));
  };

  // Flatten requests -> one row per item line (like reference image)
  const flatRows = useMemo(() => {
    const rows = [];
    for (const r of requests) {
      const items = (r.items && r.items.length > 0) ? r.items : [{
        product_code: r.product_code, product_name: r.product_name,
        quantity: r.quantity, unit: r.unit,
      }];
      items.forEach((it, idx) => {
        rows.push({
          id: r.id,
          user_id: r.user_id,
          line_id: `${r.id}-${idx}`,
          request_no: r.request_no,
          created_at: r.created_at,
          date_str: r.date_str,
          time_str: r.time_str,
          user_name: r.user_name,
          assigned_to_name: r.assigned_to_name || "",
          supplier_name: r.supplier_name,
          supplier_phone: r.supplier_phone,
          order_supplier_name: it.order_supplier_name || "",
          order_channel: it.order_channel || "",
          product_code: it.product_code,
          product_name: it.product_name,
          quantity: it.quantity,
          unit: it.unit,
          reason: r.reason,
          status: r.status,
          internal_note: r.internal_note,
          line_idx: idx + 1,
          line_count: items.length,
        });
      });
    }
    return rows;
  }, [requests]);

  // Client-side search across all visible fields
  const searched = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    if (!q) return flatRows;
    return flatRows.filter((r) =>
      (r.request_no || "").toLowerCase().includes(q) ||
      (r.product_code || "").toLowerCase().includes(q) ||
      (r.product_name || "").toLowerCase().includes(q) ||
      (r.supplier_name || "").toLowerCase().includes(q) ||
      (r.order_supplier_name || "").toLowerCase().includes(q) ||
      (r.user_name || "").toLowerCase().includes(q) ||
      (r.reason || "").toLowerCase().includes(q) ||
      (r.internal_note || "").toLowerCase().includes(q)
    );
  }, [flatRows, filters.q]);

  const sorted = useMemo(() => {
    const arr = [...searched];
    const { field, dir } = sortBy;
    arr.sort((a, b) => {
      let av = a[field], bv = b[field];
      if (av == null) av = "";
      if (bv == null) bv = "";
      if (typeof av === "number" && typeof bv === "number") {
        return dir === "asc" ? av - bv : bv - av;
      }
      return dir === "asc"
        ? String(av).localeCompare(String(bv), "tr")
        : String(bv).localeCompare(String(av), "tr");
    });
    return arr;
  }, [searched, sortBy]);

  const toggleSort = (field) => {
    setSortBy((s) => s.field === field
      ? { field, dir: s.dir === "asc" ? "desc" : "asc" }
      : { field, dir: "asc" });
  };

  // Reset to page 1 when the user changes filters/sort/pageSize — but skip the
  // initial mount + scope-resolution run, so returning from a detail page keeps
  // the page the user was on (restored from sessionStorage above).
  const pageResetReady = useRef(false);
  useEffect(() => {
    if (!scopeReady) return;
    if (!pageResetReady.current) { pageResetReady.current = true; return; }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeReady, filters.q, filters.status, filters.supplier_id, filters.user_id,
    filters.product_code, filters.date_from, filters.date_to, filters.only_open, sortBy.field, sortBy.dir, pageSize]);

  // Persist pagination/sort so navigating to a detail and back restores position.
  useEffect(() => { try { sessionStorage.setItem(SS_PAGE, String(page)); } catch (_) {} }, [page]);
  useEffect(() => { try { sessionStorage.setItem(SS_PAGE_SIZE, String(pageSize)); } catch (_) {} }, [pageSize]);
  useEffect(() => { try { sessionStorage.setItem(SS_SORT, JSON.stringify(sortBy)); } catch (_) {} }, [sortBy]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, page, pageSize]);

  const pageStart = sorted.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const pageEnd = Math.min(page * pageSize, sorted.length);

  // Clamp a restored page if the dataset shrank — only after data has loaded so
  // we never reset to page 1 while the list is still empty during initial fetch.
  useEffect(() => {
    if (!loading && sorted.length > 0 && page > totalPages) setPage(totalPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, totalPages]);

  // Unique request IDs on the current page (one request can appear as multiple item rows)
  const pageRequestIds = useMemo(() => {
    const s = new Set();
    paged.forEach((r) => {
      if (user?.role !== "user" || r.user_id === user?.id) s.add(r.id);
    });
    return Array.from(s);
  }, [paged, user]);

  const allPageSelected = pageRequestIds.length > 0 && pageRequestIds.every((id) => selected.has(id));
  const somePageSelected = pageRequestIds.some((id) => selected.has(id)) && !allPageSelected;

  const togglePageSelection = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageRequestIds.forEach((id) => next.delete(id));
      else pageRequestIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} talebi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/requests/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted} talep silindi`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Silinemedi");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkUpdateStatus = async (newStatus) => {
    if (selected.size === 0 || !newStatus) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/requests/bulk-status", {
        ids: Array.from(selected),
        status: newStatus,
      });
      toast.success(`${data.updated} talep güncellendi → ${newStatus}`);
      setBulkStatus("");
      clearSelection();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Güncellenemedi");
    } finally {
      setBulkBusy(false);
    }
  };

  const downloadExcel = () => {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.supplier_id) params.set("supplier_id", filters.supplier_id);
    if (filters.user_id) params.set("user_id", filters.user_id);
    if (filters.product_code) params.set("product_code", filters.product_code);
    if (filters.date_from) params.set("date_from", filters.date_from);
    if (filters.date_to) params.set("date_to", filters.date_to);
    if (filters.only_open) params.set("only_open", "true");
    const token = localStorage.getItem("access_token") || "";
    // Need Authorization header — use blob download via api
    api.get(`/requests/export/excel?${params.toString()}`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `talepler-${Date.now()}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.success("Excel indirildi");
      })
      .catch(() => toast.error("Excel oluşturulamadı"));
  };

  const activeChips = useMemo(() => {
    const chips = [];
    if (filters.status) chips.push({ key: "status", label: `Durum: ${filters.status}` });
    if (filters.only_open) chips.push({ key: "only_open", label: "Sadece açık" });
    if (filters.supplier_id) {
      const s = suppliers.find((x) => x.id === filters.supplier_id);
      chips.push({ key: "supplier_id", label: `Tedarikçi: ${s?.name || "?"}` });
    }
    if (filters.user_id && filters.user_id !== user?.id) {
      const u = users.find((x) => x.id === filters.user_id);
      chips.push({ key: "user_id", label: `Kullanıcı: ${u?.name || "?"}` });
    }
    if (filters.product_code) chips.push({ key: "product_code", label: `Ürün: ${filters.product_code}` });
    if (filters.date_from || filters.date_to) {
      chips.push({ key: "date", label: `Tarih: ${filters.date_from || "..."} → ${filters.date_to || "..."}` });
    }
    return chips;
  }, [filters, suppliers, users]);

  const removeChip = (key) => {
    if (key === "date") setFilters((f) => ({ ...f, date_from: "", date_to: "" }));
    else if (key === "only_open") setFilters((f) => ({ ...f, only_open: false }));
    else setFilters((f) => ({ ...f, [key]: "" }));
  };

  return (
    <PullToRefresh onRefresh={load}>
      <div className="fade-up">
        {/* Header */}
      <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Talep Takip</h1>
          <p className="text-sm text-slate-500 mt-1">
            Toplam <span data-testid="results-count" className="font-medium text-slate-900">{sorted.length}</span> ürün satırı ·{" "}
            <span className="text-slate-700">{requests.length}</span> talep · {user?.id && filters.user_id === user.id ? "benim taleplerim" : filters.user_id ? "seçili kullanıcı" : "tüm sistem"}
          </p>
        </div>
        <div className="hidden sm:flex sm:w-auto gap-2">
          <Button
            data-testid="refresh-requests-btn"
            variant="outline"
            className="h-11 gap-2 w-full sm:w-auto"
            onClick={load}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Yenile
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                data-testid="column-selector-btn"
                variant="outline"
                className="h-11 gap-2 w-full sm:w-auto"
              >
                <Settings2 className="h-4 w-4" /> Sütunlar
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-2" data-testid="column-selector-panel">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-600 px-2 py-1.5">
                Görünür Sütunlar
              </div>
              <div className="space-y-0.5 max-h-72 overflow-y-auto thin-scroll">
                {ALL_COLUMNS
                  .filter((c) => !c.adminOnly || isAdmin)
                  .map((c) => {
                    const checked = isColVisible(c.key);
                    return (
                      <button
                        key={c.key}
                        type="button"
                        data-testid={`col-toggle-${c.key}`}
                        onClick={() => !c.required && toggleCol(c.key)}
                        disabled={c.required}
                        className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm
                          ${c.required ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100 cursor-pointer"}`}
                      >
                        <span className="text-slate-700">{c.label}{c.required && <span className="text-xs text-slate-400 ml-1">(zorunlu)</span>}</span>
                        <div className={`h-4 w-4 rounded border flex items-center justify-center
                          ${checked ? "bg-slate-900 border-slate-900" : "border-slate-300"}`}>
                          {checked && <Check className="h-3 w-3 text-white" />}
                        </div>
                      </button>
                    );
                  })}
              </div>
              <button
                data-testid="col-reset"
                type="button"
                onClick={() => setVisibleCols(DEFAULT_VISIBLE)}
                className="w-full text-xs text-slate-500 hover:text-slate-900 pt-2 mt-2 border-t border-slate-100 px-2 inline-flex items-center gap-1"
              >
                <RotateCcw className="h-3 w-3" /> Varsayılana dön
              </button>
            </PopoverContent>
          </Popover>
          <Button
            data-testid="export-excel-btn"
            variant="outline"
            className="h-11 gap-2 w-full sm:w-auto border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
            onClick={downloadExcel}
          >
            <Download className="h-4 w-4" /> Excel İndir
          </Button>
          <Link to="/yeni-talep" className="w-full sm:w-auto">
            <Button data-testid="open-new-request" className="bg-slate-900 hover:bg-slate-800 h-11 px-4 w-full sm:w-auto">
              + Yeni Talep
            </Button>
          </Link>
        </div>
      </div>

      {/* Single-row filter bar (like reference image) */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 sm:p-4 mb-3">
        <div className="flex items-center gap-2 mb-3">
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 w-full sm:w-auto">
            <button
              type="button"
              data-testid="scope-mine"
              onClick={() => applyScope("mine")}
              className={`flex-1 sm:flex-none px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${user?.id && filters.user_id === user.id ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              Benim Taleplerim
            </button>
            <button
              type="button"
              data-testid="scope-all"
              onClick={() => applyScope("all")}
              className={`flex-1 sm:flex-none px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${!filters.user_id ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
            >
              Tümü
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="md:col-span-5 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              data-testid="search-input"
              placeholder="Ürün kodu, açıklama, talep no, tedarikçi ara..."
              className="pl-9 h-11"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            />
            {filters.q && (
              <button
                data-testid="search-clear"
                type="button"
                onClick={() => setFilters({ ...filters, q: "" })}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-slate-100 text-slate-400"
                aria-label="Aramayı temizle"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            type="button"
            data-testid="mobile-filters-toggle"
            onClick={() => setMobileFiltersOpen((o) => !o)}
            className="md:hidden h-11 inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white text-sm font-medium text-slate-700"
          >
            <Filter className="h-4 w-4" />
            {mobileFiltersOpen ? "Filtreleri gizle" : "Filtrele"}
            {activeChips.length > 0 && (
              <span className="bg-slate-900 text-white text-xs rounded-full px-1.5 min-w-[1.25rem] h-5 inline-flex items-center justify-center">{activeChips.length}</span>
            )}
          </button>
          <div className={`md:col-span-3 md:block ${mobileFiltersOpen ? "" : "hidden"}`}>
            <Select
              value={filters.supplier_id || "__all__"}
              onValueChange={(v) => setFilters({ ...filters, supplier_id: v === "__all__" ? "" : v })}
            >
              <SelectTrigger data-testid="filter-supplier" className="h-11">
                <SelectValue placeholder="Tüm Tedarikçiler" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tüm Tedarikçiler</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className={`md:col-span-2 md:block ${mobileFiltersOpen ? "" : "hidden"}`}>
            <Select
              value={filters.status || "__all__"}
              onValueChange={(v) => setFilters({ ...filters, status: v === "__all__" ? "" : v, only_open: false })}
            >
              <SelectTrigger data-testid="filter-status" className="h-11">
                <SelectValue placeholder="Tüm Durumlar" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tüm Durumlar</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div className={`md:col-span-2 md:block ${mobileFiltersOpen ? "" : "hidden"}`}>
              <Select
                value={filters.user_id || "__all__"}
                onValueChange={(v) => setFilters({ ...filters, user_id: v === "__all__" ? "" : v })}
              >
                <SelectTrigger data-testid="filter-user" className="h-11">
                  <SelectValue placeholder="Tüm Kullanıcılar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Tüm Kullanıcılar</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Toggle advanced filters */}
        <div className={`${mobileFiltersOpen ? "flex" : "hidden"} md:flex items-center justify-between mt-3 pt-3 border-t border-slate-100`}>
          <button
            data-testid="toggle-filters"
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5 font-medium"
          >
            <Filter className="h-4 w-4" />
            {showFilters ? "Gelişmiş filtreleri gizle" : "Gelişmiş filtreler (tarih, ürün kodu)"}
            {activeChips.length > 0 && (
              <span className="bg-slate-900 text-white text-xs rounded-full px-1.5 min-w-[1.25rem] h-5 inline-flex items-center justify-center ml-1">
                {activeChips.length}
              </span>
            )}
          </button>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none text-sm">
            <input
              type="checkbox"
              data-testid="filter-only-open"
              checked={filters.only_open}
              onChange={(e) => setFilters({ ...filters, only_open: e.target.checked, status: e.target.checked ? "" : filters.status })}
              className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
            />
            <span className="font-medium text-slate-700">Sadece açık talepler</span>
          </label>
        </div>

        {showFilters && (
          <div data-testid="advanced-filters" className="mt-3 pt-3 border-t border-slate-100 space-y-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Hızlı tarih</div>
              <div className="flex flex-wrap gap-1.5">
                {DATE_PRESETS.map((p) => (
                  <button
                    key={p.key}
                    data-testid={`date-preset-${p.key}`}
                    type="button"
                    onClick={() => applyDatePreset(p.key)}
                    className="text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Ürün Kodu</label>
                <Input
                  data-testid="filter-product-code"
                  className="mt-1.5 h-11 uppercase"
                  value={filters.product_code}
                  onChange={(e) => setFilters({ ...filters, product_code: e.target.value.toUpperCase() })}
                  placeholder="VDA-1001"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tarih (Başl.)</label>
                <Input
                  data-testid="filter-date-from"
                  type="date"
                  className="mt-1.5 h-11"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tarih (Bit.)</label>
                <Input
                  data-testid="filter-date-to"
                  type="date"
                  className="mt-1.5 h-11"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Active filter chips */}
      {activeChips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid="active-chips">
          {activeChips.map((c) => (
            <button
              key={c.key}
              data-testid={`chip-${c.key}`}
              onClick={() => removeChip(c.key)}
              className="inline-flex items-center gap-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-full px-3 py-1"
            >
              {c.label}
              <X className="h-3 w-3" />
            </button>
          ))}
          <button
            data-testid="chips-clear-all"
            onClick={() => setFilters({ ...emptyFilters, q: filters.q })}
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 rounded-full px-3 py-1"
          >
            <RotateCcw className="h-3 w-3" /> Tümünü temizle
          </button>
        </div>
      )}

      {/* Bulk action toolbar — visible to all users (each user sees only their own requests) */}
      {selected.size > 0 && (
        <div data-testid="bulk-toolbar" className="sticky top-14 z-30 mb-3 bg-slate-900 text-white rounded-lg px-4 py-2.5 flex items-center justify-between gap-3 shadow-lg flex-wrap">
          <div className="text-sm">
            <span data-testid="bulk-count" className="font-semibold">{selected.size}</span> talep seçildi
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {isAdmin && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-300">Durumu değiştir:</span>
                <Select value={bulkStatus} onValueChange={(v) => { setBulkStatus(v); bulkUpdateStatus(v); }} disabled={bulkBusy}>
                  <SelectTrigger data-testid="bulk-status-select" className="h-9 w-44 bg-white text-slate-900 border-0">
                    <SelectValue placeholder="Durum seç" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`bulk-status-opt-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <button
              data-testid="bulk-clear"
              onClick={clearSelection}
              className="text-xs text-slate-300 hover:text-white px-2 py-1.5"
            >
              Seçimi temizle
            </button>
            <Button
              data-testid="bulk-delete-btn"
              onClick={bulkDelete}
              disabled={bulkBusy}
              size="sm"
              className="bg-rose-600 hover:bg-rose-700 text-white h-9 gap-1.5"
            >
              {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Sil
            </Button>
          </div>
        </div>
      )}

      {/* ============ TABLE (desktop) ============ */}
      {loading ? (
        <div className="text-center text-slate-400 py-12 bg-white border border-slate-200 rounded-lg">Yükleniyor...</div>
      ) : sorted.length === 0 ? (
        <div data-testid="empty-state" className="text-center text-slate-500 py-12 border border-dashed border-slate-200 rounded-lg bg-white">
          {requests.length === 0 ? "Henüz talep bulunmuyor." : "Aramanızla eşleşen talep bulunamadı."}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto thin-scroll max-h-[calc(100vh-280px)]">
              <table className="w-full text-sm table-auto">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                  <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-600">
                    <th className="pl-3 pr-1 py-3 w-8 bg-slate-50">
                      <input
                        type="checkbox"
                        data-testid="select-all-page"
                        checked={allPageSelected}
                        ref={(el) => { if (el) el.indeterminate = somePageSelected; }}
                        onChange={togglePageSelection}
                        className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 cursor-pointer"
                        aria-label="Tümünü seç"
                      />
                    </th>
                    {isColVisible("request_no") && <Th label="Talep No" field="request_no" sortBy={sortBy} onSort={toggleSort} />}
                    {isColVisible("created_at") && <Th label="Tarih" field="created_at" sortBy={sortBy} onSort={toggleSort} />}
                    {isColVisible("user_name") && <Th label="Kullanıcı" field="user_name" sortBy={sortBy} onSort={toggleSort} />}
                    {isColVisible("assigned_to_name") && <Th label="Hazırlayan" field="assigned_to_name" sortBy={sortBy} onSort={toggleSort} />}
                    {isColVisible("supplier_name") && <Th label="Tedarikçi" field="supplier_name" sortBy={sortBy} onSort={toggleSort} />}
                    {isColVisible("product_code") && <Th label="Ürün Kodu" field="product_code" sortBy={sortBy} onSort={toggleSort} />}
                    {isColVisible("product_name") && <Th label="Açıklama" field="product_name" sortBy={sortBy} onSort={toggleSort} />}
                    {isColVisible("quantity") && <Th label="Miktar" field="quantity" sortBy={sortBy} onSort={toggleSort} align="right" />}
                    {isColVisible("unit") && <Th label="Birim" field="unit" sortBy={sortBy} onSort={toggleSort} />}
                    {isAdmin && isColVisible("reason") && <Th label="Sebep" field="reason" sortBy={sortBy} onSort={toggleSort} />}
                    {isColVisible("internal_note") && <Th label="İç Not" field="internal_note" sortBy={sortBy} onSort={toggleSort} />}
                    {isColVisible("status") && <Th label="Durum" field="status" sortBy={sortBy} onSort={toggleSort} sticky />}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paged.map((r) => (
                    <tr
                      key={r.line_id}
                      data-testid={`row-${r.line_id}`}
                      className={`row-hover transition-colors ${selected.has(r.id) ? "bg-slate-50" : ""}`}
                    >
                      <td className="pl-3 pr-1 py-2.5">
                        <input
                          type="checkbox"
                          data-testid={`row-select-${r.line_id}`}
                          checked={selected.has(r.id)}
                          onChange={() => toggleRow(r.id)}
                          disabled={!canSelectRow(r)}
                          className={`h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 ${canSelectRow(r) ? "cursor-pointer" : "opacity-30 cursor-not-allowed"}`}
                          aria-label="Seç"
                        />
                      </td>
                      {isColVisible("request_no") && (
                        <td className="px-2 py-2.5">
                          <Link to={`/talepler/${r.id}`} className="font-semibold text-slate-900 hover:underline whitespace-nowrap">
                            {r.request_no}
                          </Link>
                          {r.line_count > 1 && (
                            <span className="ml-1 text-xs text-slate-400">{r.line_idx}/{r.line_count}</span>
                          )}
                        </td>
                      )}
                      {isColVisible("created_at") && (
                        <td className="px-2 py-2.5 text-slate-600 whitespace-nowrap text-xs">
                          <div>{r.date_str || formatDate(r.created_at)}</div>
                          <div className="text-slate-400">{r.time_str || ""}</div>
                        </td>
                      )}
                      {isColVisible("user_name") && <td className="px-2 py-2.5 text-slate-700 text-xs"><div className="max-w-[110px] truncate" title={r.user_name}>{r.user_name}</div></td>}
                      {isColVisible("assigned_to_name") && <td className="px-2 py-2.5 text-xs" data-testid={`row-assignee-${r.line_id}`}>{r.assigned_to_name ? <span className="inline-block max-w-[120px] truncate text-teal-700 font-medium" title={r.assigned_to_name}>{r.assigned_to_name}</span> : <span className="text-slate-300">—</span>}</td>}
                      {isColVisible("supplier_name") && <td className="px-2 py-2.5 text-slate-700 text-xs"><SupplierCell row={r} /></td>}
                      {isColVisible("product_code") && <td className="px-2 py-2.5 font-medium text-slate-900 whitespace-nowrap text-xs">{r.product_code}</td>}
                      {isColVisible("product_name") && (
                        <td className="px-2 py-2.5 text-slate-700 text-xs">
                          <div className="max-w-[230px] truncate" title={r.product_name}>{r.product_name}</div>
                        </td>
                      )}
                      {isColVisible("quantity") && <td className="px-2 py-2.5 text-right font-medium text-slate-900 whitespace-nowrap">{r.quantity}</td>}
                      {isColVisible("unit") && <td className="px-2 py-2.5 text-slate-500 text-xs">{r.unit}</td>}
                      {isAdmin && isColVisible("reason") && <td className="px-2 py-2.5 text-slate-600 text-xs whitespace-nowrap">{r.reason}</td>}
                      {isColVisible("internal_note") && (
                        <td className="px-2 py-2.5 text-slate-500 text-xs">
                          <div className="max-w-[160px] truncate" title={r.internal_note || ""}>{r.internal_note || "—"}</div>
                        </td>
                      )}
                      {isColVisible("status") && <td className={`px-2 py-2.5 whitespace-nowrap sticky-status sticky right-0 z-10 border-l border-slate-100 ${selected.has(r.id) ? "bg-slate-50" : "bg-white"}`}><StatusBadge status={r.status} /></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 overflow-hidden">
            {paged.map((r) => (
              <div
                key={r.line_id}
                data-testid={`mobile-row-${r.line_id}`}
                className={`block row-hover p-3.5 transition-colors ${selected.has(r.id) ? "bg-slate-50" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    data-testid={`mobile-row-select-${r.line_id}`}
                    checked={selected.has(r.id)}
                    onChange={() => toggleRow(r.id)}
                    disabled={!canSelectRow(r)}
                    className={`h-4 w-4 mt-1 rounded border-slate-300 text-slate-900 focus:ring-slate-900 ${canSelectRow(r) ? "cursor-pointer" : "opacity-30 cursor-not-allowed"}`}
                    aria-label="Seç"
                  />
                  <Link to={`/talepler/${r.id}`} className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="font-display font-semibold text-slate-900">{r.request_no}</span>
                        {r.line_count > 1 && (
                          <span className="text-xs text-slate-400">{r.line_idx}/{r.line_count}</span>
                        )}
                      </div>
                      <StatusBadge status={r.status} />
                    </div>
                    <div className="text-sm text-slate-700">
                      <span className="font-medium text-slate-900">{r.product_code}</span> · {r.product_name}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span><span className="font-medium text-slate-700">{r.quantity}</span> {r.unit}</span>
                      <span>→ {r.order_supplier_name || r.supplier_name}</span>
                      <span>{r.date_str || formatDate(r.created_at)}</span>
                      {isAdmin && <span>{r.user_name}</span>}
                      {r.assigned_to_name && <span className="text-teal-700 font-medium">Hazırlayan: {r.assigned_to_name}</span>}
                    </div>
                    {r.internal_note && (
                      <div className="text-xs text-slate-500 mt-1 truncate">
                        <span className="font-medium text-slate-600">İç Not:</span> {r.internal_note}
                      </div>
                    )}
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {/* ============ PAGINATION ============ */}
          <div data-testid="pagination" className="mt-4 bg-white border border-slate-200 rounded-lg px-3 py-2.5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-xs text-slate-500">
              <span className="font-medium text-slate-900">{pageStart}-{pageEnd}</span> / {sorted.length} satır gösteriliyor
            </div>
            <div className="flex items-center gap-3 flex-wrap justify-center">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-slate-500">Sayfa boyutu:</span>
                <select
                  data-testid="page-size-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white focus:outline-none focus:ring-1 focus:ring-slate-900"
                >
                  {[10, 25, 50, 100, 200].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-1">
                <button
                  data-testid="page-first"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent text-slate-600"
                  aria-label="İlk sayfa"
                >
                  <ChevronsLeft className="h-4 w-4" />
                </button>
                <button
                  data-testid="page-prev"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent text-slate-600"
                  aria-label="Önceki"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <PageNumbers page={page} totalPages={totalPages} onChange={setPage} />
                <button
                  data-testid="page-next"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent text-slate-600"
                  aria-label="Sonraki"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  data-testid="page-last"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent text-slate-600"
                  aria-label="Son sayfa"
                >
                  <ChevronsRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    </PullToRefresh>
  );
}

function PageNumbers({ page, totalPages, onChange }) {
  // Build a compact page list: 1 ... (page-1) page (page+1) ... totalPages
  const nums = [];
  const add = (n) => { if (!nums.includes(n) && n >= 1 && n <= totalPages) nums.push(n); };
  add(1);
  for (let i = page - 1; i <= page + 1; i++) add(i);
  add(totalPages);
  nums.sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < nums.length; i++) {
    if (i > 0 && nums[i] - nums[i - 1] > 1) out.push("…");
    out.push(nums[i]);
  }
  return (
    <div className="flex items-center gap-0.5 mx-1">
      {out.map((n, idx) => n === "…" ? (
        <span key={`gap-${idx}`} className="px-1.5 text-slate-400 text-xs">…</span>
      ) : (
        <button
          key={n}
          data-testid={`page-num-${n}`}
          onClick={() => onChange(n)}
          className={`min-w-[28px] h-7 px-2 text-xs rounded-md font-medium ${n === page ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function SupplierCell({ row }) {
  const name = row.order_supplier_name || row.supplier_name;
  const CH = {
    whatsapp: { label: "WP", cls: "bg-green-100 text-green-700" },
    mail: { label: "Mail", cls: "bg-sky-100 text-sky-700" },
    b2b: { label: "B2B", cls: "bg-indigo-100 text-indigo-700" },
  };
  const c = row.order_supplier_name ? CH[row.order_channel] : null;
  return (
    <div className="flex items-center gap-1.5 max-w-[150px]">
      <span className="truncate" title={name}>{name}</span>
      {c && <span className={`inline-flex items-center text-[10px] font-bold uppercase px-1.5 py-0.5 rounded shrink-0 ${c.cls}`}>{c.label}</span>}
    </div>
  );
}

function Th({ label, field, sortBy, onSort, align = "left", sticky = false }) {
  const active = sortBy.field === field;
  const Icon = active ? (sortBy.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th className={`px-2 py-3 whitespace-nowrap bg-slate-50 ${align === "right" ? "text-right" : ""} ${sticky ? "sticky right-0 z-20 border-l border-slate-200" : ""}`}>
      <button
        type="button"
        data-testid={`sort-${field}`}
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 hover:text-slate-900 ${active ? "text-slate-900" : "text-slate-600"} ${align === "right" ? "flex-row-reverse" : ""}`}
      >
        {label}
        <Icon className="h-3 w-3" />
      </button>
    </th>
  );
}
